import { createComponentStore } from '../../utils/createComponentStore';
import { makeDefaultField } from '../pages/forms/fieldPreviews/fieldTypes';
import { normalizeFormCore, stripField } from '../pages/forms/fieldPreviews/normalizeFieldDefinitions';
import { buildFieldPlaceholders } from '../pages/forms/fieldPlaceholders';

const emptyData = () => ({
  fields: [],
  title: '',
  description: '',
  accessPolicy: { mode: 'PUBLIC', selectedUserIds: [] },
  responseMode: 'FILL_SUBMIT',
  formSettings: {},
  stepperTitle: '',
  flowEntryId: null,
  flowConfig: null,
  // Template reference (build inline vs. bind a top-level Form Template)
  sourceMode: 'INLINE',
  formTemplateId: null,
  pinnedVersion: null,
  fieldPlaceholders: [],
});

const { getStore: getDynamicFormStore, defaultStore } = createComponentStore({
  type: 'dynamic_form',
  endpoint: 'dynamic-form',
  registryKey: 'dynamic_form',
  initData: emptyData,
  onLoadData: (rawData) => ({
    ...emptyData(),
    ...normalizeFormCore(rawData),
    stepperTitle: rawData?.stepperTitle || '',
    flowEntryId: rawData?.flowEntryId ?? null,
    flowConfig: rawData?.flowConfig ?? null,
    sourceMode: rawData?.sourceMode === 'TEMPLATE' ? 'TEMPLATE' : 'INLINE',
    formTemplateId: rawData?.formTemplateId ?? null,
    pinnedVersion: rawData?.pinnedVersion ?? null,
    fieldPlaceholders: Array.isArray(rawData?.fieldPlaceholders) ? rawData.fieldPlaceholders : [],
  }),
  extraActions: (set, get) => ({
    setFields: (fields) => set({
      data: {
        ...get().data,
        fields: Array.isArray(fields) ? fields.map(stripField).filter(Boolean) : [],
      },
    }),
    setTitle: (title) => set({ data: { ...get().data, title } }),
    setDescription: (description) => set({ data: { ...get().data, description } }),
    setAccessPolicy: (accessPolicy) => set({ data: { ...get().data, accessPolicy } }),
    setResponseMode: (responseMode) => set({ data: { ...get().data, responseMode } }),
    setFormSettings: (formSettings) => set({ data: { ...get().data, formSettings: { ...get().data.formSettings, ...formSettings } } }),
    setStepperTitle: (stepperTitle) => set({ data: { ...get().data, stepperTitle } }),
    setFlowSettings: ({ flowEntryId, flowConfig }) => set({
      data: { ...get().data, flowEntryId: flowEntryId ?? null, flowConfig: flowConfig ?? null },
    }),
    addField: () => {
      const fields = [...get().data.fields];
      const order = fields.length;
      fields.push(makeDefaultField('text', order));
      set({ data: { ...get().data, fields } });
    },
    updateField: (index, updates) => {
      const fields = [...get().data.fields];
      fields[index] = stripField({ ...fields[index], ...updates }) || fields[index];
      set({ data: { ...get().data, fields } });
    },
    removeField: (index) => {
      const fields = get().data.fields.filter((_, i) => i !== index);
      set({ data: { ...get().data, fields } });
    },
    reorderFields: (fromIndex, toIndex) => {
      const fields = [...get().data.fields];
      const [moved] = fields.splice(fromIndex, 1);
      fields.splice(toIndex, 0, moved);
      fields.forEach((f, i) => { f.order = i; });
      set({ data: { ...get().data, fields } });
    },
    // Template-reference actions (INLINE vs TEMPLATE source mode)
    setSourceMode: (sourceMode) => set({
      data: { ...get().data, sourceMode: sourceMode === 'TEMPLATE' ? 'TEMPLATE' : 'INLINE' },
    }),
    // Bind to a published top-level Form Template. Clears local fields (template owns the
    // schema) and stamps the fieldPlaceholders snapshot for downstream autocomplete.
    setFormTemplateId: (formTemplateId, templateFields) => set({
      data: {
        ...get().data,
        sourceMode: 'TEMPLATE',
        formTemplateId: formTemplateId || null,
        fields: [],
        fieldPlaceholders: buildFieldPlaceholders(templateFields),
      },
    }),
    syncFieldPlaceholdersFromTemplate: (templateFields) => set({
      data: { ...get().data, fieldPlaceholders: buildFieldPlaceholders(templateFields) },
    }),
    // TEMPLATE → INLINE: copy the resolved template payload into local componentData once,
    // then decouple so edits stay local and template updates no longer apply.
    copyInlineFromResolvedPayload: (payload) => set({
      data: {
        ...get().data,
        ...normalizeFormCore(payload || {}),
        sourceMode: 'INLINE',
        formTemplateId: null,
        pinnedVersion: null,
        fieldPlaceholders: [],
      },
    }),
  }),
});

export { getDynamicFormStore };
export default defaultStore;
